/** Entrega física de boletas vista desde la oficina (recorrido E3). */
import { apiRequest } from "../../lib/api";

export type EstadoEntrega = "Pendiente" | "Entregada" | "NoEntregada";
export type MotivoNoEntrega =
  | "CasaCerrada"
  | "Mudanza"
  | "Rechazo"
  | "DireccionNoUbicada"
  | "Otro";

/** El motivo que no se arregla volviendo a pasar: es tarea del padrón. */
export const MOTIVO_PADRON: MotivoNoEntrega = "Mudanza";

export const MOTIVOS_NO_ENTREGA: ReadonlyArray<{
  value: MotivoNoEntrega;
  label: string;
}> = [
  { value: "CasaCerrada", label: "Casa cerrada" },
  { value: "Mudanza", label: "Se mudó / ya no vive" },
  { value: "Rechazo", label: "Rechazó recibirla" },
  { value: "DireccionNoUbicada", label: "No ubicó la dirección" },
  { value: "Otro", label: "Otro motivo" },
];

export function motivoLabel(m: MotivoNoEntrega | null): string {
  if (!m) return "";
  return MOTIVOS_NO_ENTREGA.find((x) => x.value === m)?.label ?? m;
}

export interface PeriodoEntrega {
  mes: number;
  anio: number;
  boletas: number;
}

export interface ResumenRuta {
  ruta_id: number | null;
  ruta_codigo: string | null;
  ruta_nombre: string | null;
  total: number;
  entregadas: number;
  no_entregadas: number;
  pendientes: number;
}

export interface ResumenEntregas {
  mes: number;
  anio: number;
  total: number;
  entregadas: number;
  no_entregadas: number;
  pendientes: number;
  /** Dadas por entregadas al facturar el mes siguiente: nadie pasó por ahí. */
  cerradas_por_sistema: number;
  /** No entregadas por mudanza: el padrón tiene que resolverlas. */
  requieren_padron: number;
  por_ruta: ResumenRuta[];
}

export interface EntregaEnPanel {
  lectura_id: number;
  numero_boleta: string | null;
  monto_total: number;
  mes: number;
  anio: number;

  socio_id: number;
  socio_nombre: string;
  socio_rut: string | null;

  contrato_id: number;
  arranque_id: number;
  codigo_arranque: string | null;
  direccion: string;
  ruta_id: number | null;
  ruta_codigo: string | null;
  orden_ruta: number | null;

  estado: EstadoEntrega;
  intentos: number;
  motivo_no_entrega: MotivoNoEntrega | null;
  recibido_por: string | null;
  fecha_entrega: string | null;
  observacion: string | null;
  entregado_por_id: number | null;
  cerrada_por_sistema: boolean;
  pagada: boolean;
}

export function listarPeriodosEntrega(): Promise<PeriodoEntrega[]> {
  return apiRequest<PeriodoEntrega[]>("/api/v1/entregas/periodos");
}

export function obtenerResumenEntregas(
  mes: number,
  anio: number,
): Promise<ResumenEntregas> {
  return apiRequest<ResumenEntregas>(`/api/v1/entregas/resumen?mes=${mes}&anio=${anio}`);
}

export interface FiltrosEntregas {
  mes: number;
  anio: number;
  rutaId?: number | null;
  estado?: EstadoEntrega | null;
  motivo?: MotivoNoEntrega | null;
}

export function listarEntregas(f: FiltrosEntregas): Promise<EntregaEnPanel[]> {
  const qs = new URLSearchParams({ mes: String(f.mes), anio: String(f.anio) });
  if (f.rutaId != null) qs.set("ruta_id", String(f.rutaId));
  if (f.estado) qs.set("estado", f.estado);
  if (f.motivo) qs.set("motivo", f.motivo);
  return apiRequest<EntregaEnPanel[]>(`/api/v1/entregas?${qs.toString()}`);
}

/** Marcar entregada desde el mesón (el socio pasó a pagar y se la llevó). */
export function registrarEntrega(
  lecturaId: number,
  payload: {
    estado: "Entregada" | "NoEntregada";
    recibido_por?: string;
    motivo_no_entrega?: MotivoNoEntrega;
    observacion?: string;
  },
): Promise<unknown> {
  return apiRequest(`/api/v1/entregas/lecturas/${lecturaId}`, {
    method: "POST",
    body: payload,
  });
}
